import React, { useState, useMemo } from 'react'
import { Modal, Descriptions, Button, Row, Col, Tag, Divider } from 'antd'
import moment from 'moment'
import SelectPromotionModal from './SelectPromotionModal'
import { observer } from 'mobx-react-lite'
import { useStore } from 'src/hooks'
import { debounce, isEmpty } from 'lodash'
import { formatCurrency } from 'src/helpers/functions'

const ORDER_TYPE_MAP: any = {
   SHARED: 'Xe ghép',
   COVER: 'Bao xe',
   DELIVERY: 'Gửi đồ'
}

const ConfirmBookingModal = observer(({ visible, data, onCancel, onConfirm }: any) => {
   const OrderStore = useStore('OrderStore')
   const [promoVisible, setPromoVisible] = useState(false)
   const [promotion, setPromotion] = useState<any>(null)

   // 1. Lấy giá tạm tính từ kết quả Preview API trong Store
   const tempPrice = useMemo(() => {
      return OrderStore.previewOrderData?.finalPrice || 0
   }, [OrderStore.previewOrderData])

   // 2. Logic tính toán giá trị giảm giá (Đồng bộ logic từ App Mobile)
   const discountValue = useMemo(() => {
      if (!isEmpty(promotion)) {
         if (promotion?.type === 'PERCENTAGE') {
            const discount = tempPrice * (promotion?.value / 100)
            // Nếu có giá trần (maxDiscountAmount), lấy giá trị nhỏ hơn
            return promotion?.maxDiscountAmount && discount >= promotion?.maxDiscountAmount
               ? promotion?.maxDiscountAmount
               : discount
         }
         return promotion?.value // Giảm tiền mặt cố định
      }
      return 0
   }, [promotion, tempPrice])

   // 3. Tổng tiền thanh toán cuối cùng
   const finalTotal = useMemo(() => tempPrice - discountValue, [tempPrice, discountValue])

   if (!data) return null

   const handleConfirm = () => {
      // Đóng gói payload gửi đi bao gồm thông tin khuyến mãi
      const finalPayload = {
         ...data,
         promotionCode: promotion?.code || null,
         promotionDiscount: discountValue
      }
      onConfirm(finalPayload)
      setPromotion(null)
   }

   const handleClose = () => {
      setPromotion(null)
      onCancel()
   }

   return (
      <>
         <Modal
            visible={visible}
            width={800}
            onCancel={handleClose}
            onOk={debounce(handleConfirm, 500)}
            okText="ĐẶT CHUYẾN NGAY"
            cancelText="QUAY LẠI"
            okButtonProps={{
               style: { backgroundColor: '#EDBB1D', color: '#000', fontWeight: 'bold', border: 'none' }
            }}
            cancelButtonProps={{
               style: {
                  backgroundColor: '#fff',
                  color: '#000',
                  border: '1px solid #cdc',
                  fontWeight: '400'
               }
            }}
         >
            <h3 style={{ color: '#EDBB1D', borderBottom: '2px solid #EDBB1D', paddingBottom: 8, fontWeight: 'bold' }}>
               XÁC NHẬN CHUYẾN ĐI
            </h3>

            <Descriptions column={2} size="small" style={{ marginTop: 16 }}>
               <Descriptions.Item label="Dịch vụ">
                  <Tag color="blue" style={{ fontWeight: 'bold' }}>
                     {ORDER_TYPE_MAP[data?.selectedTab]}
                  </Tag>
               </Descriptions.Item>

               <Descriptions.Item label="Lộ trình">
                  <span style={{ fontWeight: 500 }}>
                     {data?.tripSelected?.fromDistrict?.name || data?.from?.name} →{' '}
                     {data?.tripSelected?.toDistrict?.name || data?.to?.name}
                  </span>
               </Descriptions.Item>

               <Descriptions.Item label={'Điểm đón'}>
                  <span style={{ fontWeight: 500 }}> {data?.pickupLocationAddress}</span>
               </Descriptions.Item>
               <Descriptions.Item label={'Điểm trả'}>
                  <span style={{ fontWeight: 500 }}>{data?.dropoffLocationAddress}</span>
               </Descriptions.Item>
               {data.selectedTab === 'DELIVERY' ? (
                  <Descriptions.Item label="Thời gian gửi">
                     <span style={{ fontWeight: 500 }}></span>
                     {moment(data?.departureDateTime).format('HH:mm DD/MM/YYYY')}
                  </Descriptions.Item>
               ) : (
                  <>
                     <Descriptions.Item label="Thời gian đi">
                        <span style={{ fontWeight: 500 }}></span>
                        {moment(data?.departureDateTime).format('HH:mm DD/MM/YYYY')}
                     </Descriptions.Item>

                     {data?.returnDateTime && (
                        <Descriptions.Item label="Thời gian về">
                           <span style={{ fontWeight: 500 }}>
                              {moment(data?.returnDateTime).format('HH:mm DD/MM/YYYY')}
                           </span>
                        </Descriptions.Item>
                     )}
                  </>
               )}

               <Descriptions.Item label="Khách hàng">
                  <span style={{ fontWeight: 500 }}>
                     {data?.customerName} — {data?.customerPhone}
                  </span>
               </Descriptions.Item>
               {data.passengerCount && (
                  <Descriptions.Item label="Số hành khách">
                     <span style={{ fontWeight: 500 }}>
                        {data?.passengerCount ? `${data.passengerCount} người` : '—'}
                     </span>
                  </Descriptions.Item>
               )}
               {data.carType && (
                  <Descriptions.Item label="Loại xe">
                     <span style={{ fontWeight: 500 }}>{data?.carType ? `${data.carType} chỗ` : '—'}</span>
                  </Descriptions.Item>
               )}
               <Descriptions.Item label="Ghi chú" span={2}>
                  <span style={{ fontWeight: '500', color: '#000' }}>{data?.notes || 'Không có ghi chú'}</span>
               </Descriptions.Item>
            </Descriptions>

            {/* PHẦN TÍNH TOÁN GIÁ - STYLE GIỐNG MOBILE */}
            <div
               style={{
                  marginTop: 8,
                  padding: 24,
                  background: '#fafafa',
                  borderRadius: 8,
                  border: '1px solid #f0f0f0'
               }}
            >
               <Row justify="space-between" style={{ marginBottom: 12 }}>
                  <span style={{ color: '#666' }}>Tạm tính</span>
                  <span style={{ fontWeight: 500 }}>{formatCurrency(tempPrice, true)} đ</span>
               </Row>

               <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                  <span style={{ color: '#666' }}>Khuyến mãi</span>
                  <div>
                     {promotion ? (
                        <div style={{ textAlign: 'right' }}>
                           <Tag color="green" closable onClose={() => setPromotion(null)}>
                              {promotion.code} ( -{formatCurrency(discountValue, true)} đ)
                           </Tag>
                           <Button type="link" size="small" onClick={() => setPromoVisible(true)}>
                              Đổi mã
                           </Button>
                        </div>
                     ) : (
                        <Button type="primary" ghost size="small" onClick={() => setPromoVisible(true)}>
                           Chọn mã giảm giá
                        </Button>
                     )}
                  </div>
               </Row>

               <Divider style={{ margin: '12px 0' }} />

               <Row justify="space-between" align="middle">
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>Thanh toán</span>
                  <span style={{ fontWeight: 'bold', fontSize: '24px', color: '#EDBB1D' }}>
                     {formatCurrency(finalTotal, true)} đ
                  </span>
               </Row>
            </div>
         </Modal>

         <SelectPromotionModal
            visible={promoVisible}
            onClose={() => setPromoVisible(false)}
            onSubmit={(promo) => {
               setPromotion(promo)
               setPromoVisible(false)
            }}
            selectedDefault={promotion}
         />
      </>
   )
})

export default ConfirmBookingModal
